import { useEffect, useState } from "react";
import { Sparkles, Check, FileText, Download, Loader2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const VALUES = ["Wake", "24-CVD-9988", "Jane Smith", "$820 / mo"];
const DEMO = {
  en: { desc: "Change my child support in an existing Wake County order — my income dropped.", fields: ["County", "File No.", "Movant", "Current Support"],
    steps: ["Describe", "AI reads", "Auto-fills", "Download"], bar: "AI Assistant", tell: "Tell us what you need", btn: "Find & fill my form",
    reading: "Reading {forms} NC forms…", matching: "Matching AOC-CV-600 · Motion to Modify Child Support", filling: "auto-filling", done: "Court-ready PDF complete", dl: "Download" },
  es: { desc: "Cambiar la manutención de mi hijo en una orden existente del condado de Wake — mis ingresos bajaron.", fields: ["Condado", "N.º de expediente", "Solicitante", "Manutención actual"],
    steps: ["Describa", "La IA lee", "Autocompleta", "Descargue"], bar: "Asistente de IA", tell: "Díganos qué necesita", btn: "Buscar y llenar mi formulario",
    reading: "Leyendo {forms} formularios de NC…", matching: "Coincide con AOC-CV-600 · Moción para modificar la manutención", filling: "autocompletando", done: "PDF listo para el tribunal", dl: "Descargar" },
  vi: { desc: "Thay đổi tiền cấp dưỡng con theo lệnh hiện có của Quận Wake — thu nhập của tôi đã giảm.", fields: ["Quận", "Số hồ sơ", "Người nộp đơn", "Cấp dưỡng hiện tại"],
    steps: ["Mô tả", "AI đọc", "Tự điền", "Tải xuống"], bar: "Trợ lý AI", tell: "Cho chúng tôi biết bạn cần gì", btn: "Tìm & điền mẫu đơn",
    reading: "Đang đọc {forms} mẫu đơn NC…", matching: "Khớp AOC-CV-600 · Đơn yêu cầu thay đổi cấp dưỡng con", filling: "đang tự điền", done: "PDF sẵn sàng nộp tòa", dl: "Tải xuống" },
  fr: { desc: "Modifier la pension alimentaire de mon enfant dans une ordonnance existante du comté de Wake — mes revenus ont baissé.", fields: ["Comté", "N° de dossier", "Requérant", "Pension actuelle"],
    steps: ["Décrivez", "L'IA lit", "Remplit", "Téléchargez"], bar: "Assistant IA", tell: "Dites-nous ce dont vous avez besoin", btn: "Trouver et remplir mon formulaire",
    reading: "Lecture de {forms} formulaires de NC…", matching: "Correspondance AOC-CV-600 · Requête en modification de pension", filling: "remplissage auto", done: "PDF prêt pour le tribunal", dl: "Télécharger" },
  ar: { desc: "تعديل نفقة طفلي في أمر قائم من مقاطعة ويك — انخفض دخلي.", fields: ["المقاطعة", "رقم الملف", "مقدّم الطلب", "النفقة الحالية"],
    steps: ["صِف", "الذكاء يقرأ", "تعبئة تلقائية", "تنزيل"], bar: "المساعد الذكي", tell: "أخبرنا بما تحتاجه", btn: "ابحث عن نموذجي واملأه",
    reading: "قراءة {forms} نموذج من نماذج NC…", matching: "مطابقة AOC-CV-600 · طلب تعديل نفقة الطفل", filling: "تعبئة تلقائية", done: "ملف PDF جاهز للمحكمة", dl: "تنزيل" },
  hmn: { desc: "Hloov kuv tus nqi yug menyuam hauv daim ntawv txiav txim uas twb muaj nyob hauv Lub Cheeb Nroog Wake — kuv cov nyiaj tau los poob qis lawm.", fields: ["Cheeb nroog", "Tus lej ntaub ntawv", "Tus thov", "Nqi yug menyuam tam sim no"],
    steps: ["Piav qhia", "AI nyeem", "Sau rau", "Rub tawm"], bar: "AI Pab", tell: "Qhia peb seb koj xav tau dab tsi", btn: "Nrhiav thiab sau kuv daim ntawv",
    reading: "Tab tom nyeem {forms} daim ntawv NC…", matching: "Phim AOC-CV-600 · Daim ntawv thov hloov nqi yug menyuam", filling: "tab tom sau", done: "PDF npaj txhij rau tsev hais plaub", dl: "Rub tawm" },
};
const SCENE_MS = [3600, 1600, 3400, 2600];

export default function DemoVideo() {
  const { lang, forms } = useI18n();
  const D = DEMO[lang] || DEMO.en;
  const reading = D.reading.split("{forms}").join(forms);
  const DESCRIPTION = D.desc;
  const FIELDS = D.fields.map((label, i) => ({ label, value: VALUES[i] }));
  const [scene, setScene] = useState(0);
  const [typed, setTyped] = useState(0);
  const [revealed, setRevealed] = useState(0);

  // scene loop
  useEffect(() => {
    const t = setTimeout(() => setScene((s) => (s + 1) % 4), SCENE_MS[scene]);
    return () => clearTimeout(t);
  }, [scene]);

  // typewriter for scene 0
  useEffect(() => {
    if (scene !== 0) { setTyped(0); return; }
    let i = 0;
    const t = setInterval(() => {
      i += 1; setTyped(i);
      if (i >= DESCRIPTION.length) clearInterval(t);
    }, 45);
    return () => clearInterval(t);
  }, [scene, DESCRIPTION]);

  // staggered field reveal for scene 2
  useEffect(() => {
    if (scene !== 2) { setRevealed(0); return; }
    let i = 0;
    const t = setInterval(() => {
      i += 1; setRevealed(i);
      if (i >= VALUES.length) clearInterval(t);
    }, 550);
    return () => clearInterval(t);
  }, [scene]);

  const steps = D.steps;

  return (
    <div className="grid-card overflow-hidden select-none" data-testid="demo-video">
      {/* browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 bg-primary">
        <span className="w-3 h-3 rounded-full bg-white/30" />
        <span className="w-3 h-3 rounded-full bg-white/30" />
        <span className="w-3 h-3 rounded-full bg-accent" />
        <div className="ms-3 flex items-center gap-2 text-primary-foreground/70 text-xs">
          <Sparkles size={12} className="text-accent" /> nclegalforms.com — {D.bar}
        </div>
      </div>

      {/* progress dots */}
      <div className="flex items-center gap-1.5 px-5 pt-4">
        {steps.map((label, i) => (
          <div key={label} className="flex-1">
            <div className={`h-1 rounded-full transition-colors duration-500 ${i <= scene ? "bg-accent" : "bg-border"}`} />
            <span className={`text-[0.6rem] uppercase tracking-wider transition-colors ${i === scene ? "text-accent font-semibold" : "text-muted-foreground"}`}>{label}</span>
          </div>
        ))}
      </div>

      {/* stage */}
      <div className="p-5 h-[300px] bg-background">
        {scene === 0 && (
          <div className="animate-fade-up">
            <p className="overline text-muted-foreground mb-2">{D.tell}</p>
            <div className="border border-border rounded-sm p-4 min-h-[120px] text-[15px] leading-relaxed">
              {DESCRIPTION.slice(0, typed)}
              <span className="inline-block w-0.5 h-4 bg-accent align-middle animate-pulse ms-0.5" />
            </div>
            <div className="mt-4 inline-flex items-center gap-2 bg-accent text-accent-foreground text-sm px-4 py-2 rounded-sm">
              <Sparkles size={14} /> {D.btn}
            </div>
          </div>
        )}

        {scene === 1 && (
          <div className="h-full grid place-items-center animate-fade-up">
            <div className="text-center">
              <Loader2 size={34} className="text-accent animate-spin mx-auto mb-4" />
              <p className="font-serif text-2xl">{reading}</p>
              <p className="text-sm text-muted-foreground mt-1">{D.matching}</p>
            </div>
          </div>
        )}

        {scene === 2 && (
          <div className="animate-fade-up">
            <p className="overline text-accent mb-3 flex items-center gap-1"><Sparkles size={12} /> AOC-CV-600 · {D.filling}</p>
            <div className="space-y-2.5">
              {FIELDS.map((f, i) => (
                <div key={f.label} className={`flex items-center justify-between border border-border rounded-sm px-3 py-2 transition-all duration-500 ${i < revealed ? "opacity-100 translate-x-0" : "opacity-0 translate-x-3"}`}>
                  <span className="text-xs text-muted-foreground">{f.label}</span>
                  <span className="flex items-center gap-2 text-sm font-medium">
                    {f.value}
                    {i < revealed && <Check size={14} className="text-accent" />}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {scene === 3 && (
          <div className="h-full grid place-items-center animate-fade-up">
            <div className="text-center">
              <div className="relative inline-block mb-4">
                <FileText size={46} className="text-primary mx-auto" />
                <span className="absolute -right-2 -bottom-2 grid place-items-center w-6 h-6 bg-accent text-accent-foreground rounded-full"><Check size={14} /></span>
              </div>
              <p className="font-serif text-2xl">{D.done}</p>
              <div className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                <Download size={14} /> {D.dl} AOC-CV-600.pdf
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
